import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '@/lib/utils';
import { FilterCategory } from './FilterList';
import { FilterListItem } from './FilterListItem';

interface FilterListCategoryProps {
    category: FilterCategory;
}

const MAX_VISIBLE_ITEMS = 10;
const ITEM_LINE_HEIGHT_PX = 20;
const ITEM_GAP_PX = 16;
const MAX_VISIBLE_HEIGHT_PX =
    MAX_VISIBLE_ITEMS * ITEM_LINE_HEIGHT_PX +
    (MAX_VISIBLE_ITEMS - 1) * ITEM_GAP_PX;

export function FilterListCategory({ category }: FilterListCategoryProps) {
    const isScrollable = category.items.length > MAX_VISIBLE_ITEMS;

    return (
        <div className="flex flex-col gap-4">
            <div className="flex items-center justify-between">
                <span className="text-sm-medium text-white-100">
                    {category.name}
                </span>
                {category.onClearAll && (
                    <button
                        onClick={category.onClearAll}
                        className="text-sm-medium text-grey-300 hover:text-white-100 transition-colors"
                    >
                        Clear
                    </button>
                )}
            </div>
            <div
                className={cn(
                    'flex flex-col gap-4',
                    isScrollable && 'overflow-y-auto scrollbar-grey-600 pr-2',
                )}
                style={
                    isScrollable
                        ? { maxHeight: MAX_VISIBLE_HEIGHT_PX }
                        : undefined
                }
            >
                <AnimatePresence>
                    {category.items.map((item, index) => {
                        // For smooth animation - stable key for single-item categories and item-scoped key for multi-item categories
                        const key =
                            category.items.length <= 1
                                ? category.name
                                : item.id || item.label;

                        return (
                            <motion.div
                                key={key}
                                initial={{ opacity: 0, height: 0, y: -10 }}
                                animate={{ opacity: 1, height: 'auto', y: 0 }}
                                exit={{ opacity: 0, height: 0, y: -10 }}
                                transition={{ duration: 0.1 }}
                            >
                                <FilterListItem
                                    item={item}
                                    showRemoveButton={category.items.length > 1}
                                />
                            </motion.div>
                        );
                    })}
                </AnimatePresence>
            </div>
        </div>
    );
}
